// react and js
import PropTypes from "prop-types";
import { React, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// mui
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Typography,
  Collapse,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// components
import backendRequest from "../../components/BackendRequest";
import CardOutlet from "../../components/CardOutlet";
import LoadingAnimation from "../../components/LoadingAnimation";
import MissingContent from "../../components/MissingContent";

// hooks
import appContext from "../../hooks/appContext";
import { stateStorageInit } from "../../hooks/stateStorageInit";

// layouts

// pages

// theme
import {
  B1Tiny,
  B3Tiny,
  FetchTableLinearProgress,
  FetchTablePagination,
  FetchTableTextField,
  FetchTableSlider,
} from "../../theme/styled";

// utils
import { TranslatedMessage } from "../../utils/common";

// widgets

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function FetchTable({ tableArray, refreshLoading, isAdministrator }) {
  FetchTable.propTypes = {
    tableArray: PropTypes.any.isRequired,
    refreshLoading: PropTypes.any.isRequired,
    isAdministrator: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return isDesktop ? (
    <>
      <TableContainer component={Box} sx={{ background: "white", borderRadius: "10px 10px 13px 13px" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                width="10%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              >
                <Typography variant="contentSmallBold"> {t("distributorPortal.customers.headers.id")} </Typography>
              </TableCell>
              <TableCell
                width="15%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              >
                <Typography variant="contentSmallBold"> {t("distributorPortal.customers.headers.forename")} </Typography>
              </TableCell>
              <TableCell
                width="15%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              >
                <Typography variant="contentSmallBold"> {t("distributorPortal.customers.headers.surname")} </Typography>
              </TableCell>
              <TableCell
                width="45%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              >
                <Typography variant="contentSmallBold"> {t("distributorPortal.customers.headers.email")} </Typography>
              </TableCell>
              <TableCell
                width="15%"
                sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
              />
            </TableRow>
          </TableHead>
          {refreshLoading === false ? (
            <TableBody>
              {tableArray.map((row, index) => (
                <TableRow key={index} sx={{ mb: 1 }}>
                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="left"
                  >
                    <Typography variant="contentSmall"> {row[0]} </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="left"
                  >
                    <Typography
                      variant="contentSmall"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {isAdministrator ? row[9] : row[7]}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="left"
                  >
                    <Typography
                      variant="contentSmall"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {isAdministrator ? row[10] : row[8]}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="left"
                  >
                    <Typography
                      variant="contentSmall"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {isAdministrator ? row[6] : row[4]}
                    </Typography>
                  </TableCell>

                  <TableCell
                    sx={{ borderBottom: 3, borderColor: "#F2F2F2" }}
                    align="left"
                  >
                    <B3Tiny
                      fullWidth
                      variant="outlined"
                      onClick={() => {
                        window.scrollBy(0, 0);
                        navigate(`/distributor-portal/application/${row[0]}`);
                      }}
                    >
                      {t("distributorPortal.common.manage")}
                    </B3Tiny>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : null}
        </Table>
      </TableContainer>

      {refreshLoading === true ? (
        <Box
          sx={{
            justifyContent: "center",
            px: 2,
            pt: "19px",
            pb: "18px",
            background: "white",
          }}
        >
          <FetchTableLinearProgress color="inherit" sx={{ width: "100%" }} />
        </Box>
      ) : null}

      {tableArray.length === 0 && refreshLoading === false ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "left",
            px: 2,
            pt: "9px",
            pb: "9px",
            background: "white",
          }}
        >
          <Typography variant="contentSmall">
            {t("distributorPortal.messages.noDataForFilters")}
          </Typography>
        </Box>
      ) : null}
    </>
  ) : (
    <>
      {refreshLoading === false ? (
        <>
          {tableArray.map((row, index) => (
            <Stack
              key={index}
              spacing="3px"
              sx={{ background: "white", mb: 2, px: "10px", py: "10px" }}
            >
              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "30%", pr: 1 }}>
                  <Typography variant="contentSmallBold"> {t("distributorPortal.customers.headers.id")} </Typography>
                </Box>
                <Box sx={{ width: "70%", pl: 1 }}>
                  <Typography variant="contentSmall">{row[0]}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "30%", pr: 1 }}>
                  <Typography variant="contentSmallBold"> {t("distributorPortal.customers.headers.forename")} </Typography>
                </Box>
                <Box sx={{ width: "70%", pl: 1 }}>
                  <Typography
                    variant="contentSmall"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {isAdministrator ? row[9] : row[8]}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "30%", pr: 1 }}>
                  <Typography variant="contentSmallBold"> {t("distributorPortal.customers.headers.surname")} </Typography>
                </Box>
                <Box sx={{ width: "70%", pl: 1 }}>
                  <Typography
                    variant="contentSmall"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {isAdministrator ? row[10] : row[9]}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "30%", pr: 1 }}>
                  <Typography variant="contentSmallBold"> {t("distributorPortal.customers.headers.email")} </Typography>
                </Box>
                <Box sx={{ width: "70%", pl: 1 }}>
                  <Typography
                    variant="contentSmall"
                    sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {isAdministrator ? row[6] : row[5]}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex" }}>
                <Box sx={{ width: "100%" }}>
                  <B3Tiny
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      window.scrollBy(0, 0);
                      navigate(`/distributor-portal/application/${row[0]}`);
                    }}
                  >
                    {t("distributorPortal.common.manage")}
                  </B3Tiny>
                </Box>
              </Box>
            </Stack>
          ))}
        </>
      ) : null}

      {refreshLoading === true ? (
        <Box
          sx={{
            justifyContent: "center",
            px: 2,
            pt: "19px",
            pb: "18px",
            background: "white",
          }}
        >
          <FetchTableLinearProgress color="inherit" sx={{ width: "100%" }} />
        </Box>
      ) : null}

      {tableArray.length === 0 && refreshLoading === false ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "left",
            px: 2,
            pt: "9px",
            pb: "9px",
            background: "white",
          }}
        >
          <Typography variant="contentSmall">
            {t("distributorPortal.messages.noDataForFilters")}
          </Typography>
        </Box>
      ) : null}
    </>
  );
}

function WidgetMainContentProcessed({
  content,
  error,
  warning,
  success,
  returnMessage,
  refreshLoading,
  setRefreshLoading,
  filterId,
  setFilterId,
  filterForename,
  setFilterForename,
  filterSurname,
  setFilterSurname,
  filterEmail,
  setFilterEmail,
  filterCountry,
  setFilterCountry,
  mobileBackOpen,
}) {
  WidgetMainContentProcessed.propTypes = {
    content: PropTypes.any.isRequired,
    error: PropTypes.any.isRequired,
    warning: PropTypes.any.isRequired,
    success: PropTypes.any.isRequired,
    returnMessage: PropTypes.any.isRequired,
    refreshLoading: PropTypes.any.isRequired,
    setRefreshLoading: PropTypes.any.isRequired,
    filterId: PropTypes.any.isRequired,
    setFilterId: PropTypes.any.isRequired,
    filterForename: PropTypes.any.isRequired,
    setFilterForename: PropTypes.any.isRequired,
    filterSurname: PropTypes.any.isRequired,
    setFilterSurname: PropTypes.any.isRequired,
    filterEmail: PropTypes.any.isRequired,
    setFilterEmail: PropTypes.any.isRequired,
    filterCountry: PropTypes.any.isRequired,
    setFilterCountry: PropTypes.any.isRequired,
    mobileBackOpen: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // eslint-disable-next-line
  const { stateStorage, setStateStorage } = useContext(appContext);
  const myLocalSettingStorage = JSON.parse(
    localStorage.getItem("myLocalSettingStorage")
  );

  let distributorPortalApplicationCustomersTableFilterCollapseInitValue = false;
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfsee608a5915392cd7 !== null &&
      myLocalSettingStorage.obfsee608a5915392cd7 !== undefined
    ) {
      distributorPortalApplicationCustomersTableFilterCollapseInitValue =
        myLocalSettingStorage.obfsee608a5915392cd7;
    }
  }
  const [collapse, setCollapse] = useState(
    distributorPortalApplicationCustomersTableFilterCollapseInitValue
  );
  useEffect(() => {
    if (
      filterId !== "" ||
      filterForename !== "" ||
      filterSurname !== "" ||
      filterEmail !== "" ||
      filterCountry !== ""
    ) {
      setCollapse(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let distributorPortalApplicationCustomersTableFilterItemsPerPageInitValue =
    "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfs4c600de4f4f0573b !== null &&
      myLocalSettingStorage.obfs4c600de4f4f0573b !== undefined
    ) {
      distributorPortalApplicationCustomersTableFilterItemsPerPageInitValue =
        myLocalSettingStorage.obfs4c600de4f4f0573b;
    }
  }
  const [itemsPerPages, setItemsPerPages] = useState(
    distributorPortalApplicationCustomersTableFilterItemsPerPageInitValue
  );

  const [currentPage, setCurrentPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);
  useEffect(() => {
    if (content.num_rows !== undefined) {
      setTotalPages(Math.ceil(content.num_rows / itemsPerPages));
      const initValueTotalPages = Math.ceil(content.num_rows / itemsPerPages);
      if (initValueTotalPages <= 0) {
        setTotalPages(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content]);

  const [localFilterId, setLocalFilterId] = useState(filterId);
  const [localFilterForename, setLocalFilterForename] =
    useState(filterForename);
  const [localFilterSurname, setLocalFilterSurname] = useState(filterSurname);
  const [localFilterEmail, setLocalFilterEmail] = useState(filterEmail);
  const [localFilterCountry, setLocalFilterCountry] = useState(filterCountry);

  const contentFiltered = [];
  if (content.t_accounts !== undefined) {
    content.t_accounts.data.forEach((account, index) => {
      if (
        index >= (currentPage - 1) * itemsPerPages &&
        index <= currentPage * itemsPerPages - 1
      ) {
        contentFiltered.push(account);
      }
    });
  }

  function setFilters() {
    setCurrentPage(1);

    setFilterId(localFilterId);
    setFilterForename(localFilterForename);
    setFilterSurname(localFilterSurname);
    setFilterEmail(localFilterEmail);
    setFilterCountry(localFilterCountry);

    setRefreshLoading(true);

    setStateStorage((previousState) => ({
      ...previousState,
      widgetUpdateDistributorPortalDistributionCustomers: true,
    }));
  }

  let isAdministrator = false;
  if (
    stateStorage.userInformation.account_settings.config_data
      .config_data_administrator !== undefined
  ) {
    isAdministrator = true;
  }

  const [mobileCollapse, setMobileCollapse] = useState(mobileBackOpen);
  useEffect(() => {
    if (mobileBackOpen === false) {
      setMobileCollapse(isDesktop);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12} lg={12}>
        <CardOutlet
          variant="outlet"
          backgroundProp="#F2F2F2"
          title={t("distributorPortal.customers.title")}
          subheader={t("distributorPortal.customers.subheader")}
          error={error}
          warning={warning}
          success={success}
          returnMessage={returnMessage}
          mainOutlet={
            content.message.includes("no application distribution") ? null : (
              <Box sx={{ mb: !mobileCollapse ? -2 : 0 }}>
                {isDesktop === false ? (
                  <B3Tiny
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                    onClick={() => {
                      setMobileCollapse(!mobileCollapse);
                    }}
                  >
                    {mobileCollapse ? t("distributorPortal.common.close") : t("distributorPortal.common.open")}
                  </B3Tiny>
                ) : null}

                <Collapse
                  sx={{ mx: -1, px: 1 }}
                  in={mobileCollapse}
                  timeout="auto"
                  unmountOnExit
                >
                  <B3Tiny
                    fullWidth
                    variant="outlined"
                    sx={{ mb: 2 }}
                    onClick={() => {
                      setCollapse(!collapse);

                      const newLocalSettingStorage = JSON.parse(
                        localStorage.getItem("myLocalSettingStorage")
                      );

                      newLocalSettingStorage.obfsee608a5915392cd7 = !collapse;
                      localStorage.setItem(
                        "myLocalSettingStorage",
                        JSON.stringify(newLocalSettingStorage)
                      );
                    }}
                  >
                    {collapse ? t("distributorPortal.filters.hide") : t("distributorPortal.filters.show")}
                  </B3Tiny>

                  <Collapse
                    sx={{ mx: -1 }}
                    in={collapse}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box sx={{ flexGrow: 1, mx: 1, mb: 2 }}>
                      <Grid container rowSpacing={1} columnSpacing={2}>
                        <Grid item xs={6} md={6} lg={6}>
                          <Typography variant="contentSmallBold">{t("distributorPortal.customers.headers.id")}</Typography>
                          <FetchTableTextField
                            value={localFilterId}
                            onChange={(event) => {
                              setLocalFilterId(event.target.value);

                              const newLocalSettingStorage = JSON.parse(
                                localStorage.getItem("myLocalSettingStorage")
                              );

                              newLocalSettingStorage.obfs56152826f5e79efa =
                                event.target.value;
                              localStorage.setItem(
                                "myLocalSettingStorage",
                                JSON.stringify(newLocalSettingStorage)
                              );
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") setFilters();
                            }}
                            fullWidth
                            size="small"
                            sx={{ input: { textAlign: "center" }, mb: "0px" }}
                          />
                        </Grid>

                        <Grid item xs={6} md={6} lg={6}>
                          <Typography variant="contentSmallBold">
                            {t("distributorPortal.customers.headers.forename")}
                          </Typography>
                          <FetchTableTextField
                            value={localFilterForename}
                            onChange={(event) => {
                              setLocalFilterForename(event.target.value);

                              const newLocalSettingStorage = JSON.parse(
                                localStorage.getItem("myLocalSettingStorage")
                              );

                              newLocalSettingStorage.obfse81f2b9b35f6c1ee =
                                event.target.value;
                              localStorage.setItem(
                                "myLocalSettingStorage",
                                JSON.stringify(newLocalSettingStorage)
                              );
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") setFilters();
                            }}
                            fullWidth
                            size="small"
                            sx={{ input: { textAlign: "center" }, mb: "0px" }}
                          />
                        </Grid>

                        <Grid item xs={6} md={6} lg={6}>
                          <Typography variant="contentSmallBold">
                            {t("distributorPortal.customers.headers.surname")}
                          </Typography>
                          <FetchTableTextField
                            value={localFilterSurname}
                            onChange={(event) => {
                              setLocalFilterSurname(event.target.value);

                              const newLocalSettingStorage = JSON.parse(
                                localStorage.getItem("myLocalSettingStorage")
                              );

                              newLocalSettingStorage.obfsc539eee8491fc3e4 =
                                event.target.value;
                              localStorage.setItem(
                                "myLocalSettingStorage",
                                JSON.stringify(newLocalSettingStorage)
                              );
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") setFilters();
                            }}
                            fullWidth
                            size="small"
                            sx={{ input: { textAlign: "center" }, mb: "0px" }}
                          />
                        </Grid>

                        <Grid item xs={6} md={6} lg={6}>
                          <Typography variant="contentSmallBold">{t("distributorPortal.customers.headers.email")}</Typography>
                          <FetchTableTextField
                            value={localFilterEmail}
                            onChange={(event) => {
                              setLocalFilterEmail(event.target.value);

                              const newLocalSettingStorage = JSON.parse(
                                localStorage.getItem("myLocalSettingStorage")
                              );

                              newLocalSettingStorage.obfs3b11728390a578a5 =
                                event.target.value;
                              localStorage.setItem(
                                "myLocalSettingStorage",
                                JSON.stringify(newLocalSettingStorage)
                              );
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") setFilters();
                            }}
                            fullWidth
                            size="small"
                            sx={{ input: { textAlign: "center" }, mb: "0px" }}
                          />
                        </Grid>

                        <Grid item xs={12} md={12} lg={12}>
                          <Typography variant="contentSmallBold">
                            {t("distributorPortal.customers.headers.country")}
                          </Typography>
                          <FetchTableTextField
                            value={localFilterCountry}
                            onChange={(event) => {
                              setLocalFilterCountry(event.target.value);

                              const newLocalSettingStorage = JSON.parse(
                                localStorage.getItem("myLocalSettingStorage")
                              );

                              newLocalSettingStorage.obfs0f54405943bd0fb8 =
                                event.target.value;
                              localStorage.setItem(
                                "myLocalSettingStorage",
                                JSON.stringify(newLocalSettingStorage)
                              );
                            }}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") setFilters();
                            }}
                            fullWidth
                            size="small"
                            sx={{ input: { textAlign: "center" }, mb: "0px" }}
                          />
                        </Grid>

                        <Grid item xs={12} md={12} lg={12}>
                          <Typography variant="contentSmallBold">
                            {t("distributorPortal.filters.itemsPerPage")}
                          </Typography>
                          <Box sx={{ px: 1 }}>
                            <FetchTableSlider
                              size="small"
                              min={1}
                              max={100}
                              align="right"
                              value={itemsPerPages}
                              onChange={(event, newValue) => {
                                setItemsPerPages(newValue);
                                setTotalPages(
                                  Math.ceil(content.num_rows / newValue)
                                );
                                setCurrentPage(1);

                                const newLocalSettingStorage = JSON.parse(
                                  localStorage.getItem("myLocalSettingStorage")
                                );

                                newLocalSettingStorage.obfs4c600de4f4f0573b =
                                  newValue;
                                localStorage.setItem(
                                  "myLocalSettingStorage",
                                  JSON.stringify(newLocalSettingStorage)
                                );
                              }}
                              valueLabelDisplay="auto"
                            />
                          </Box>
                        </Grid>
                      </Grid>

                      <B1Tiny
                        fullWidth
                        variant="contained"
                        sx={{ mt: 1 }}
                        onClick={() => {
                          setFilters();
                        }}
                      >
                        {t("distributorPortal.filters.setAndRefresh")}
                      </B1Tiny>
                    </Box>
                  </Collapse>

                  <Box sx={{ mt: 0, mb: 1 }} />

                  <FetchTable
                    tableArray={contentFiltered}
                    refreshLoading={refreshLoading}
                    isAdministrator={isAdministrator}
                  />

                  <Stack sx={{ mt: 3 }} alignItems="center">
                    <FetchTablePagination
                      count={totalPages}
                      variant="outlined"
                      shape="rounded"
                      page={currentPage}
                      onChange={(event, value) => {
                        setCurrentPage(value);
                      }}
                    />
                  </Stack>
                </Collapse>
              </Box>
            )
          }
        />
      </Grid>
    </Grid>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function Widget({
  distributor,
  loadingWidgetDistributionCustomers,
  setLoadingWidgetDistributionCustomers,
  mobileBackOpen,
}) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  Widget.propTypes = {
    distributor: PropTypes.any.isRequired,
    loadingWidgetDistributionCustomers: PropTypes.any.isRequired,
    setLoadingWidgetDistributionCustomers: PropTypes.any.isRequired,
    mobileBackOpen: PropTypes.any.isRequired,
  };

  const { t } = useTranslation();
  const { stateStorage, setStateStorage } = useContext(appContext);
  const myLocalSettingStorage = JSON.parse(
    localStorage.getItem("myLocalSettingStorage")
  );

  const [error, setError] = useState(false);
  const [warning, setWarning] = useState(false);
  const [success, setSuccess] = useState(false);

  const [loading, setLoading] = useState(navigator.onLine);
  const [content, setContent] = useState();
  const [returnMessage, setReturnMessage] = useState("");

  const [regularRefreshIntervalCounter, setRegularRefreshIntervalCounter] =
    useState(0);

  // Filter
  let distributorPortalApplicationCustomersTableFilterIdInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfs56152826f5e79efa !== null &&
      myLocalSettingStorage.obfs56152826f5e79efa !== undefined
    ) {
      distributorPortalApplicationCustomersTableFilterIdInitValue =
        myLocalSettingStorage.obfs56152826f5e79efa;
    }
  }
  const [filterId, setFilterId] = useState(
    distributorPortalApplicationCustomersTableFilterIdInitValue
  );

  // Filter
  let distributorPortalApplicationCustomersTableFilterForenameInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfse81f2b9b35f6c1ee !== null &&
      myLocalSettingStorage.obfse81f2b9b35f6c1ee !== undefined
    ) {
      distributorPortalApplicationCustomersTableFilterForenameInitValue =
        myLocalSettingStorage.obfse81f2b9b35f6c1ee;
    }
  }
  const [filterForename, setFilterForename] = useState(
    distributorPortalApplicationCustomersTableFilterForenameInitValue
  );

  // Filter
  let distributorPortalApplicationCustomersTableFilterSurnameInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfsc539eee8491fc3e4 !== null &&
      myLocalSettingStorage.obfsc539eee8491fc3e4 !== undefined
    ) {
      distributorPortalApplicationCustomersTableFilterSurnameInitValue =
        myLocalSettingStorage.obfsc539eee8491fc3e4;
    }
  }
  const [filterSurname, setFilterSurname] = useState(
    distributorPortalApplicationCustomersTableFilterSurnameInitValue
  );

  // Filter
  let distributorPortalApplicationCustomersTableFilterEmailInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfs3b11728390a578a5 !== null &&
      myLocalSettingStorage.obfs3b11728390a578a5 !== undefined
    ) {
      distributorPortalApplicationCustomersTableFilterEmailInitValue =
        myLocalSettingStorage.obfs3b11728390a578a5;
    }
  }
  const [filterEmail, setFilterEmail] = useState(
    distributorPortalApplicationCustomersTableFilterEmailInitValue
  );

  // Filter
  let distributorPortalApplicationCustomersTableFilterCountryInitValue = "";
  if (myLocalSettingStorage !== null) {
    if (
      myLocalSettingStorage.obfs0f54405943bd0fb8 !== null &&
      myLocalSettingStorage.obfs0f54405943bd0fb8 !== undefined
    ) {
      distributorPortalApplicationCustomersTableFilterCountryInitValue =
        myLocalSettingStorage.obfs0f54405943bd0fb8;
    }
  }
  const [filterCountry, setFilterCountry] = useState(
    distributorPortalApplicationCustomersTableFilterCountryInitValue
  );

  const [refreshLoading, setRefreshLoading] = useState(false);

  // ######################################################
  // Main backend request
  // ######################################################
  function MainRequest(periodic) {
    if (
      localStorage.getItem("myReauthentication") === "false" &&
      navigator.onLine === true
    ) {
      if (distributor !== 0) {
        if (periodic === false) {
          setStateStorage((previousState) => ({
            ...previousState,
            widgetUpdateDistributorPortalDistributionCustomers: false,
          }));

          setError(false);
          setWarning(false);
          setSuccess(false);
          setReturnMessage("");
        }

        const sendData = {
          request_type: "dashboard",
          route_info: "distributor_portal",
          widget: "customer_and_account_management",
          operation: "fetch",
          client: {
            dashboard_access_token:
              stateStorage.userInformation.answer.dashboard_access_token,
          },
          admin: {
            selected_distributor_id: distributor,
          },
          selection: {
            page_start: "",
            page_count: "",
            page_size: "",
            page_order_type: "desc",
            page_order_by: "dashboard_id",
          },
          filters: {
            dashboard_id: filterId,
            forename: filterForename,
            surname: filterSurname,
            email: filterEmail,
            country: filterCountry,
          },
        };
        backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData)
          .then((contentFromBackend) => {
const keyOnly = Object.keys(contentFromBackend);
            const checkKey = keyOnly.includes("message");

            if (checkKey === true) {
              if (contentFromBackend.message === "success") {
                setError(false);
                setLoading(false);
                setLoadingWidgetDistributionCustomers(false);
                setWarning(false);
                setSuccess(false);
                setRefreshLoading(false);
                setContent(contentFromBackend);
                setReturnMessage("");
              } else if (
                contentFromBackend.message.includes(
                  "no application distribution"
                )
              ) {
                setSuccess(true);
                setWarning(false);
                setContent(contentFromBackend);
                setLoading(false);
                setLoadingWidgetDistributionCustomers(false);
                setRefreshLoading(false);
                setReturnMessage(
                  t("distributorPortal.messages.applicationCustomersNotFound")
                );
              } else if (contentFromBackend.message === "token invalid") {
                setSuccess(false);
                setWarning(true);
                setError(false);
                setLoading(false);
                setLoadingWidgetDistributionCustomers(false);
                setRefreshLoading(false);
                setReturnMessage(<TranslatedMessage tKey="distributorPortal.messages.loggedOut" />);

                setStateStorage(stateStorageInit);
                localStorage.setItem("forcedLoggedOut", true);

                localStorage.setItem("myLocalStateStorage", JSON.stringify(""));
                localStorage.removeItem("myLocalStateStorage");

                sessionStorage.setItem("myLocalLastRoute", JSON.stringify(""));
                sessionStorage.removeItem("myLocalLastRoute");

                localStorage.setItem(
                  "myLocalSettingStorage",
                  JSON.stringify("")
                );
                localStorage.removeItem("myLocalSettingStorage");

                localStorage.setItem("myReauthentication", JSON.stringify(""));
                localStorage.removeItem("myReauthentication");
              } else {
                setSuccess(false);
                setWarning(false);
                setError(true);
                setLoading(false);
                setLoadingWidgetDistributionCustomers(false);
                setRefreshLoading(false);
                setReturnMessage(<TranslatedMessage tKey="distributorPortal.messages.invalidRequest" />);
              }
            } else {
              setSuccess(false);
              setWarning(false);
              setError(true);
              setLoading(false);
              setLoadingWidgetDistributionCustomers(false);
              setRefreshLoading(false);
              setReturnMessage(<TranslatedMessage tKey="distributorPortal.common.unknownError" />);
            }
          })
          .catch(() => {
            if (navigator.onLine === true) {
              setError(true);
              setSuccess(false);
              setWarning(false);
              setLoading(false);
              setLoadingWidgetDistributionCustomers(false);
              setRefreshLoading(false);
              setReturnMessage(<TranslatedMessage tKey="distributorPortal.common.unknownError" />);
            }
          });
      }
    }
  }

  // ######################################################
  // Refresh if distributor changes
  // ######################################################
  useEffect(() => {
    MainRequest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributor]);

  // ######################################################
  // Inital and triggered refreshs
  // ######################################################
  useEffect(() => {
    if (!stateStorage.widgetUpdateDistributorPortalDistributionCustomers)
      return;

    MainRequest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateStorage.widgetUpdateDistributorPortalDistributionCustomers]);

  // ######################################################
  // Regular refreshs
  // ######################################################
  useEffect(() => {
    const timerID = setTimeout(() => {
      setRegularRefreshIntervalCounter(regularRefreshIntervalCounter + 1);
    }, stateStorage.widgetUpdateIntervalDistributorPortalDistributionCustomers);

    return () => {
      clearTimeout(timerID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRefreshIntervalCounter]);

  useEffect(() => {
    MainRequest(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRefreshIntervalCounter]);

  // ######################################################
  // Data representation
  // ######################################################
  let WidgetOutlet = (
    <Box sx={{ mt: 3 }}>
      <MissingContent message={t("distributorPortal.customers.currentTitle")} />
    </Box>
  );
  if (loading || loadingWidgetDistributionCustomers) {
    WidgetOutlet = (
      <Box sx={{ mt: 3 }}>
        <LoadingAnimation />
      </Box>
    );
  } else if (error) {
    WidgetOutlet = (
      <Box sx={{ mt: 3 }}>
        <MissingContent message={t("distributorPortal.customers.currentTitle")} />
      </Box>
    );
  } else if (content) {
    if (content.hide_widget === false) {
      WidgetOutlet = (
        <Box sx={{ mt: 3 }}>
          <WidgetMainContentProcessed
            content={content}
            error={error}
            warning={warning}
            success={success}
            returnMessage={returnMessage}
            refreshLoading={refreshLoading}
            setRefreshLoading={setRefreshLoading}
            filterId={filterId}
            setFilterId={setFilterId}
            filterForename={filterForename}
            setFilterForename={setFilterForename}
            filterSurname={filterSurname}
            setFilterSurname={setFilterSurname}
            filterEmail={filterEmail}
            setFilterEmail={setFilterEmail}
            filterCountry={filterCountry}
            setFilterCountry={setFilterCountry}
            mobileBackOpen={mobileBackOpen}
          />
        </Box>
      );
    } else {
      WidgetOutlet = null;
    }
  }

  // ######################################################
  // Outlet
  // ######################################################
  return WidgetOutlet;
}
